/**
 * Bottom-sheet modal showing the full details for one event.
 *
 * Opened from the events feed when an EventCard is tapped. Renders the
 * flyer image, title,
 * kind, date/time, country, venue, source link, and the RSVP button so
 * users don't have to dismiss the sheet to commit to going.
 */

import { useEffect, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Flag, PhotoPlaceholder } from '@/components/primitives';
import type { Country, Event, FlagPalette, Venue } from '@/types/domain';
import { eventShareUrl, shareLink } from '@/lib/shareLinks';
import { EVENTS, track } from '@/lib/analytics';
import { haptics } from '@/lib/native';

interface Props {
  event: Event;
  country: Country | undefined;
  venue: Venue | undefined;
  /** Resolved teams when the event is linked to a match. When both are
   * present the meta row renders "Home vs Away" instead of the single
   * country chip, which would otherwise read like a location tag. */
  matchHome: Country | undefined;
  matchAway: Country | undefined;
  isGoing: boolean;
  onToggle: () => void;
  onClose: () => void;
}

const DEFAULT_PALETTE: FlagPalette = {
  primary: '#0a0a0a',
  secondary: '#7a7268',
  tertiary: '#d8d4cb',
};

function isSameDay(a: string, b: string): boolean {
  return new Date(a).toDateString() === new Date(b).toDateString();
}

function formatDate(starts: string): string {
  return new Date(starts).toLocaleString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
}

// Same-day events get the hierarchy: just times in the range under the date.
function formatSameDayTime(starts: string, ends: string | null): string {
  const startStr = new Date(starts).toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
  if (!ends) return startStr;
  const endStr = new Date(ends).toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
  return `${startStr} → ${endStr}`;
}

// Cross-day events render as two ranges (date range + time range) so they
// match the same-day hierarchy without repeating any value across the two
// lines. Apple Wallet / Maps use this pattern consistently.
function formatCrossDayDateRange(starts: string, ends: string): string {
  const opts: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  };
  return `${new Date(starts).toLocaleString(undefined, opts)} → ${new Date(ends).toLocaleString(undefined, opts)}`;
}

function formatCrossDayTimeRange(starts: string, ends: string): string {
  const opts: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit' };
  return `${new Date(starts).toLocaleTimeString(undefined, opts)} – ${new Date(ends).toLocaleTimeString(undefined, opts)}`;
}

// Drag past this threshold (in px) to commit to dismissing the sheet;
// anything less snaps back to the resting position.
const DISMISS_THRESHOLD_PX = 100;

export function EventDetailSheet({ event, country, venue, matchHome, matchAway, isGoing, onToggle, onClose }: Props) {
  const { t } = useTranslation();
  const [dragY, setDragY] = useState(0);
  const [dragging, setDragging] = useState(false);
  // Brief "Copied" confirmation after the clipboard fallback (desktop, where
  // there's no native share sheet). Cleared on a timer.
  const [copied, setCopied] = useState(false);
  const dragStartY = useRef<number | null>(null);
  const palette = country?.colors ?? DEFAULT_PALETTE;
  const venueName = venue?.name ?? event.venueName ?? '';
  const venueHood = venue?.hood ?? event.venueHood ?? '';
  const venueLine = [venueName, venueHood].filter(Boolean).join(' · ');

  const onDragStart = (e: ReactPointerEvent) => {
    dragStartY.current = e.clientY;
    setDragging(true);
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onDragMove = (e: ReactPointerEvent) => {
    if (dragStartY.current === null) return;
    const delta = e.clientY - dragStartY.current;
    if (delta > 0) setDragY(delta);
  };
  const endDrag = () => {
    if (dragY > DISMISS_THRESHOLD_PX) onClose();
    else setDragY(0);
    dragStartY.current = null;
    setDragging(false);
  };

  async function onShare() {
    haptics.light();
    const url = eventShareUrl(event.id);
    track(EVENTS.sharePressed, {
      surface: 'event_detail',
      eventId: event.id,
      matchId: event.matchId ?? undefined,
      venueId: event.venueId ?? undefined,
      countryCode: event.countryCode ?? undefined,
    });
    const result = await shareLink({
      url,
      title: event.title,
      text: t('events:shareText', { title: event.title }),
    });
    if (result === 'shared' || result === 'copied') haptics.success();
    if (result === 'copied') setCopied(true);
    track(EVENTS.shareCompleted, { result, surface: 'event_detail', eventId: event.id });
  }

  // Clear the "Copied" confirmation a moment after it shows.
  useEffect(() => {
    if (!copied) return;
    const id = window.setTimeout(() => setCopied(false), 1800);
    return () => window.clearTimeout(id);
  }, [copied]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  return (
    <>
      <div
        role="presentation"
        onClick={onClose}
        style={backdropStyle}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={event.title}
        style={{
          ...sheetStyle,
          transform: `translateY(${dragY}px)`,
          transition: dragging ? 'none' : 'transform 0.22s ease',
        }}
      >
        {/* Grabber + image act as the drag handle. Dragging more than
            DISMISS_THRESHOLD_PX commits to closing; otherwise it snaps
            back. Pointer events cover both touch and mouse. */}
        <div
          onPointerDown={onDragStart}
          onPointerMove={onDragMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          style={{ touchAction: 'none', cursor: dragging ? 'grabbing' : 'grab' }}
        >
          <div style={grabberStyle} />

          <div
            style={{
              position: 'relative',
              overflow: 'hidden',
              // Reserve the image slot before the network image arrives,
              // and tint the placeholder with the country palette so the
              // load-in feels intentional instead of like a layout shift.
              minHeight: event.imageUrl ? 240 : undefined,
              background: event.imageUrl
                ? `linear-gradient(135deg, ${palette.primary}22, ${palette.secondary}22)`
                : undefined,
            }}
          >
            {event.imageUrl ? (
              <>
                {/* Blurred copy of the image fills the "letterbox" bars
                    next to portrait flyers so the photo extends into its
                    own color instead of being punched out of a black slab. */}
                <img
                  aria-hidden="true"
                  alt=""
                  src={event.imageUrl}
                  decoding="async"
                  style={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    filter: 'blur(36px) saturate(1.2)',
                    transform: 'scale(1.2)',
                    opacity: 0.7,
                  }}
                />
                <img
                  src={event.imageUrl}
                  alt=""
                  decoding="async"
                  style={{
                    position: 'relative',
                    width: '100%',
                    height: 'auto',
                    maxHeight: 480,
                    objectFit: 'contain',
                    display: 'block',
                  }}
                />
              </>
            ) : (
              <PhotoPlaceholder height={180} palette={palette} />
            )}
            <div style={kindChipStyle(palette)}>{t('events:kind.' + event.kind + '.label')}</div>
            {event.isFree && <div style={freeBadgeStyle}>{t('common:free')}</div>}
            {/* Share button — top-right over the flyer. Shares the event's
                short link (`/e/<id>`); the link unfurls with a preview card and
                opens this event when tapped. Sits clear of the free badge. */}
            <button
              type="button"
              onClick={onShare}
              aria-label={t('events:shareAria', { title: event.title })}
              style={shareButtonStyle}
            >
              {copied ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M20 6 9 17l-5-5" />
                </svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M4 12v7a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-7" />
                  <path d="M16 6l-4-4-4 4" />
                  <path d="M12 2v13" />
                </svg>
              )}
            </button>
          </div>
        </div>

        <div style={{ padding: '18px 20px 20px' }}>
          <h1 style={titleStyle}>{event.title}</h1>

          {/* Date / time cluster. Same-day → date + time range.
              Cross-day → date range + time range. No value repeats. */}
          {event.endsAt && !isSameDay(event.startsAt, event.endsAt) ? (
            <>
              <div style={dateStyle}>
                {formatCrossDayDateRange(event.startsAt, event.endsAt)}
              </div>
              <div style={timeStyle}>
                {formatCrossDayTimeRange(event.startsAt, event.endsAt)}
              </div>
            </>
          ) : (
            <>
              <div style={dateStyle}>{formatDate(event.startsAt)}</div>
              <div style={timeStyle}>{formatSameDayTime(event.startsAt, event.endsAt)}</div>
            </>
          )}

          {/* Venue cluster: name (medium) + hood (muted). Spacing
              alone separates this from the date/time cluster — dividers
              were costing too much vertical room on small viewports.
              When we have a maps URL, the venue name itself is the link
              (Apple Maps card pattern); otherwise it's plain text. */}
          {venueLine && (() => {
            const mapsUrl = venue?.googleMapsUrl ?? event.venueMapUrl ?? null;
            return (
              <div style={{ marginTop: 14 }}>
                {venueName && (mapsUrl ? (
                  <a
                    href={mapsUrl}
                    target="_blank"
                    rel="noreferrer"
                    style={{ ...venueNameStyle, color: '#0a0a0a', textDecoration: 'none' }}
                  >
                    {venueName} <span style={{ opacity: 0.55 }}>{t('events:mapsArrow')}</span>
                  </a>
                ) : (
                  <div style={venueNameStyle}>{venueName}</div>
                ))}
                {venueHood && <div style={venueHoodStyle}>{venueHood}</div>}
              </div>
            );
          })()}

          {/* When the event is tied to a real match (both teams
              resolved), show the matchup — "🇧🇷 Brazil vs Argentina 🇦🇷"
              — instead of the single country chip, which would otherwise
              read like a location tag.

              When there's no match, fall back to "Cheering for 🇵🇹
              Portugal" so the meaning is explicit (this event is *for*
              that country's fans, not *located in* the country).

              Source link rides in the same row so it never falls below
              the primary CTA on tall flyers. */}
          {(matchHome && matchAway) || country || event.sourceUrl ? (
            <div style={{ ...metaRowStyle, marginTop: 10, gap: 10, flexWrap: 'wrap' }}>
              {matchHome && matchAway ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <span>{matchHome.name}</span>
                  <Flag code={matchHome.code} w={18} h={12} radius={2} />
                  <span style={{ opacity: 0.6 }}>{t('common:vs')}</span>
                  <Flag code={matchAway.code} w={18} h={12} radius={2} />
                  <span>{matchAway.name}</span>
                </span>
              ) : country ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <span>{t('events:cheeringFor')}</span>
                  <Flag code={country.code} w={18} h={12} radius={2} />
                  <span>{country.name}</span>
                </span>
              ) : null}
              {(matchHome && matchAway) || country ? (
                event.sourceUrl && <span aria-hidden="true" style={{ opacity: 0.4 }}>·</span>
              ) : null}
              {event.sourceUrl && (
                <a
                  href={event.sourceUrl}
                  target="_blank"
                  rel="noreferrer"
                  style={sourceLinkStyle}
                >
                  {t('events:seeOriginalPost')}
                </a>
              )}
            </div>
          ) : null}

          <button
            type="button"
            onClick={onToggle}
            style={{
              ...rsvpButtonStyle,
              // Consistent green Going state across all events (SST-1).
              background: isGoing ? '#0a7f0a' : '#0a0a0a',
              boxShadow: isGoing ? '0 4px 14px #0a7f0a66' : 'none',
            }}
          >
            {isGoing ? t('common:goingCheck') : t('common:imGoing')}
          </button>
        </div>

      </div>
    </>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────
const backdropStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.55)',
  zIndex: 100,
};

const sheetStyle: CSSProperties = {
  position: 'fixed',
  left: 0,
  right: 0,
  bottom: 0,
  // Match the app's warm cream so the sheet feels like part of the same
  // surface as the events feed behind it, instead of punching a white hole.
  background: '#f0eee9',
  borderTopLeftRadius: 20,
  borderTopRightRadius: 20,
  maxHeight: '92vh',
  overflowY: 'auto',
  zIndex: 101,
  paddingBottom: 'env(safe-area-inset-bottom, 0px)',
  boxShadow: '0 -10px 30px rgba(0,0,0,0.18)',
};

const grabberStyle: CSSProperties = {
  width: 40,
  height: 4,
  background: '#d8d4cb',
  borderRadius: 999,
  margin: '10px auto 6px',
};

const titleStyle: CSSProperties = {
  fontFamily: '-apple-system, system-ui',
  fontSize: 24,
  fontWeight: 600,
  margin: '2px 0 12px',
  color: '#0a0a0a',
  lineHeight: 1.2,
  letterSpacing: -0.3,
};

const dateStyle: CSSProperties = {
  fontFamily: '-apple-system, system-ui',
  fontSize: 17,
  fontWeight: 600,
  color: '#0a0a0a',
  marginBottom: 2,
};

const timeStyle: CSSProperties = {
  fontFamily: '-apple-system, system-ui',
  fontSize: 14,
  fontWeight: 500,
  color: '#6f6a60',
};

const venueNameStyle: CSSProperties = {
  fontFamily: '-apple-system, system-ui',
  fontSize: 16,
  fontWeight: 500,
  color: '#0a0a0a',
  marginBottom: 2,
};

const venueHoodStyle: CSSProperties = {
  fontFamily: '-apple-system, system-ui',
  fontSize: 14,
  fontWeight: 400,
  color: '#6f6a60',
};

const metaRowStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  fontFamily: '-apple-system, system-ui',
  fontSize: 13,
  fontWeight: 500,
  color: '#6f6a60',
};


const rsvpButtonStyle: CSSProperties = {
  display: 'block',
  width: '100%',
  border: 'none',
  color: '#fff',
  padding: '14px 18px',
  borderRadius: 12,
  fontFamily: '-apple-system, system-ui',
  fontSize: 14,
  fontWeight: 800,
  textTransform: 'uppercase',
  letterSpacing: 0.6,
  cursor: 'pointer',
  marginTop: 16,
  transition: 'all .2s',
};

const sourceLinkStyle: CSSProperties = {
  fontFamily: '-apple-system, system-ui',
  fontSize: 13,
  fontWeight: 500,
  color: '#0a0a0a',
  textDecoration: 'underline',
  textUnderlineOffset: 2,
};

const shareButtonStyle: CSSProperties = {
  position: 'absolute',
  top: 12,
  right: 12,
  width: 38,
  height: 38,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 999,
  border: 'none',
  background: 'rgba(255,255,255,0.95)',
  color: '#0a0a0a',
  boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
  cursor: 'pointer',
};

function kindChipStyle(palette: FlagPalette): CSSProperties {
  return {
    position: 'absolute',
    top: 14,
    left: 14,
    background: 'rgba(255,255,255,0.95)',
    padding: '5px 10px',
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 700,
    color: '#0a0a0a',
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    borderLeft: `3px solid ${palette.primary}`,
  };
}

const freeBadgeStyle: CSSProperties = {
  position: 'absolute',
  top: 14,
  right: 56,
  background: '#0a7f0a',
  color: '#fff',
  padding: '5px 10px',
  borderRadius: 6,
  fontSize: 11,
  fontWeight: 800,
  textTransform: 'uppercase',
  letterSpacing: 0.6,
};

